<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('addGlobalUnitForm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    <v-card-text>
      <v-form>
        <v-text-field
            v-model="form.name"
            :label="$t('addGlobalUnitForm.labels.name')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="nameErrors"
            @keyup="form.isUnitNameExists = false"
        />

        <v-text-field
            v-model="form.address"
            :label="$t('addGlobalUnitForm.labels.address')"
            type="text"
            maxlength="600"
            :counter="600"
            :error-messages="addressErrors"
        />
        <v-responsive>
          <v-row>
            <v-col cols="12" sm="6" md="12" class="py-2">
              <v-autocomplete v-model="form.organizationInn"
                                :items="organizations"
                                :label="$t('addGlobalUnitForm.labels.organization')"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="inn"/>
            </v-col>
            <v-spacer/>
          </v-row>
        </v-responsive>

        <v-card class="mx-auto">
          <v-card-text>
            <div>{{ $t('addGlobalUnitForm.labels.regions') }}</div>
            <v-row>
              <v-col cols="12" md="12" lg="4" class="pa-0"
                     v-for="item in form.selectedRegions"
                     :key="item.region.id"
              >
                <v-checkbox
                    v-model="item.allowed"
                    class="mt-2 pa-2"
                    :label="item.region.name"
                    :hide-details="true"
                    :error-messages="regionErrors"
                    dense
                />
              </v-col>
            </v-row>
            <div v-if="!anyCheckboxSelected" class="error--text">
              {{ $t('addGlobalUnitForm.errors.regionRequired') }}
            </div>
          </v-card-text>
        </v-card>

        <v-snackbar v-model="form.isUnitNameExists"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.unitExists') }}
        </v-snackbar>

        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('addGlobalUnitForm.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" :disabled="!allowSave" @click="onAddButtonClicked">{{ $t('addGlobalUnitForm.actions.add') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, required} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import _ from "underscore";
import GlobalUnitService from "@/services/global.unit.service";

export default {
  name: "AddGlobalUnitComponent",
  props: {
    organizations: {
      type: Array,
      default: () => {
        return []
      }
    },
    allowedRegions: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      loading: false,
      total: 0,

      filters: {
        inn: null
      },
      form: {
        name: '',
        isHead: false,
        address: '',
        organizationInn: '',
        selectedRegions: [],

        isUnitNameExists: false
      },
      isServerError: false
    }
  },
  computed: {
    nameErrors() {
      const errors = []

      if (this.$v.form.name.$dirty) {
        if (!this.$v.form.name.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.nameRequired'))
        } else {
          !this.$v.form.name.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.nameTooLong'))
        }
      }

      return errors
    },
    addressErrors() {
      const errors = []

      if (this.$v.form.address.$dirty) {
        if (!this.$v.form.address.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.addressRequired'))
        } else {
          !this.$v.form.address.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.addressTooLong'))
        }
      }

      return errors
    },
    regionErrors() {
      const errors = []

      if (this.$v.form.selectedRegions.$dirty) {
        if (!this.$v.form.selectedRegions.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        } else {
          !this.$v.form.selectedRegions.minLength && errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        }
      }

      return errors
    },
    anyCheckboxSelected() {
      return _.filter(this.form.selectedRegions, r => {
        return r.allowed
      }).length > 0
    },
    allowSave() {
      let allow = this.anyCheckboxSelected
      return allow
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(400)
      },
      address: {
        required,
        maxLength: maxLength(600)
      },
      selectedRegions: {
        required,
        minLength: minLength(1)
      }
    }
  },
  methods: {
    clear() {
      this.$v.$reset()
      this.form.name = ''
      this.form.isHead = false
      this.form.address = ''
      this.form.organizationInn = ''
      this.form.selectedRegions = _.map(this.allowedRegions, region => {
        return {
          region: region,
          allowed: false
        }
      })

      this.form.isUsernameExists = false
    },

    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onAddButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        const checkedRegions = _.filter(this.form.selectedRegions, ar => {
          return ar.allowed
        })

        this.form.isUnitNameExists = false
        this.isServerError = false

        GlobalUnitService.checkUnit(this.form.name)
            .then(response => {
              if (response.data.IsExists) {
                this.form.isUnitNameExists = true
                this.loading = false
                this.$store.dispatch(LAYOUT_LOADING_STOP)
              } else {
                GlobalUnitService.addUnit({
                  unitName: this.form.name,
                  organizationInn: this.form.organizationInn,
                  isHead: this.form.isHead,
                  address: this.form.address,
                  region: _.map(checkedRegions, ar => {
                    return ar.region
                  })
                })
                    .then(() => {
                      this.clear()
                      this.$emit('added')
                    }, () => {
                      this.isServerError = true
                    })
                    .finally(() => {
                      this.loading = false
                      this.$store.dispatch(LAYOUT_LOADING_STOP)
                    })
              }
            }, () => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  },
  created() {
    this.clear()
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.load()
      }
    }
  }
}
</script>

<style scoped>

</style>